import type { Landmarks, Vec2 } from '@store/handStore';

/**
 * If the incoming wrist-landmark jumps more than this in normalized image
 * coords from the last smoothed wrist, treat it as a fresh hand and
 * reseed — not an EMA update. Prevents the "skeleton flies across the
 * frame" ghosting when MediaPipe swaps handedness labels (the same
 * physical hand suddenly shows up on the other HandId's smoother) or
 * detection skips a hand for a few frames and a new one appears.
 */
const SMOOTHER_JUMP_RESEED = 0.12;

/**
 * Exponential moving average smoother for a hand's 21 landmarks.
 * Keeps gesture thresholds stable against MediaPipe jitter.
 */
export class LandmarkSmoother {
  private buf: Vec2[] | null = null;
  constructor(private readonly alpha = 0.55) {}

  push(next: Landmarks): Landmarks {
    if (!this.buf || this.buf.length !== next.length) {
      this.buf = next.map((p) => ({ x: p.x, y: p.y, z: p.z ?? 0 }));
      return this.buf;
    }
    // Detect hand-identity jumps: the wrist is the most stable anchor,
    // so we use its displacement as the signal. Any big hop → reseed.
    const wristPrev = this.buf[0]!;
    const wristNext = next[0]!;
    if (wristNext) {
      const dx = wristNext.x - wristPrev.x;
      const dy = wristNext.y - wristPrev.y;
      if (dx * dx + dy * dy > SMOOTHER_JUMP_RESEED * SMOOTHER_JUMP_RESEED) {
        for (let i = 0; i < next.length; i++) {
          const incoming = next[i]!;
          this.buf[i] = { x: incoming.x, y: incoming.y, z: incoming.z ?? 0 };
        }
        return this.buf;
      }
    }
    const a = this.alpha;
    for (let i = 0; i < next.length; i++) {
      const cur = this.buf[i]!;
      const incoming = next[i]!;
      cur.x = a * incoming.x + (1 - a) * cur.x;
      cur.y = a * incoming.y + (1 - a) * cur.y;
      const iz = incoming.z ?? 0;
      const cz = cur.z ?? 0;
      cur.z = a * iz + (1 - a) * cz;
    }
    return this.buf;
  }

  reset(): void {
    this.buf = null;
  }
}
